import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Package } from "lucide-react";

const REGIONS = [
  { value: "all", label: "Todas las zonas" },
  { value: "centro", label: "Zona Centro" },
  { value: "norte", label: "Zona Norte" },
  { value: "bajio", label: "Bajío" },
  { value: "otros", label: "Otros estados" },
];

function availabilityInfo(a) {
  if (a.status === "in_use") return { text: "En uso / fecha por confirmar", tone: "amber" };
  if (a.status === "maintenance") return { text: "En mantenimiento", tone: "red" };
  if (!a.available_date) return { text: "Disponible hoy", tone: "green" };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(`${a.available_date}T00:00:00`);
  const diff = Math.round((d - today) / 86400000);
  if (diff <= 0) return { text: "Disponible hoy", tone: "green" };
  if (diff < 30) return { text: `Disponible en ${diff} días`, tone: "blue" };
  const months = Math.round(diff / 30);
  return { text: `Disponible en ${months} ${months === 1 ? "mes" : "meses"}`, tone: "blue" };
}

const TONE = {
  green: "bg-[#10B981]/10 text-[#047857] border-[#10B981]/30",
  blue: "bg-[#2563EB]/10 text-[#1D4ED8] border-[#2563EB]/30",
  amber: "bg-[#F59E0B]/10 text-[#92400E] border-[#F59E0B]/40",
  red: "bg-[#E11D48]/10 text-[#9F1239] border-[#E11D48]/30",
};

export default function SearchAsset() {
  const nav = useNavigate();
  const [filters, setFilters] = useState({ q: "", project_id: "all", region: "all", status: "all", availability: "all" });
  const [projects, setProjects] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      const params = { is_surplus: false };
      if (filters.q) params.q = filters.q;
      if (filters.project_id !== "all") params.project_id = filters.project_id;
      if (filters.region !== "all") params.region = filters.region;
      if (filters.status !== "all") params.status = filters.status;
      if (filters.availability !== "all") params.availability = filters.availability;
      api.get("/assets", { params }).then(({ data }) => setAssets(data)).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [filters]);

  return (
    <div className="space-y-6" data-testid="search-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C6A7D]">Función 02</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0A2540] tracking-tighter mt-1">Equipamiento provisional.</h1>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E2E8F0] p-4 rounded-sm grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="relative lg:col-span-2">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6A7D]" />
          <Input
            placeholder="Buscar por nombre de activo…"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            className="pl-9 rounded-sm border-[#E2E8F0] h-10"
            data-testid="search-input"
          />
        </div>
        <Select value={filters.region} onValueChange={(v) => setFilters({ ...filters, region: v })}>
          <SelectTrigger className="rounded-sm h-10 border-[#E2E8F0]" data-testid="filter-region"><SelectValue placeholder="Zona del proyecto" /></SelectTrigger>
          <SelectContent>{REGIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.project_id} onValueChange={(v) => setFilters({ ...filters, project_id: v })}>
          <SelectTrigger className="rounded-sm h-10 border-[#E2E8F0]" data-testid="filter-project"><SelectValue placeholder="Proyecto" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los proyectos</SelectItem>
            {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E2E8F0] rounded-sm overflow-x-auto" data-testid="results-scroll">
        <table className="w-full min-w-[480px] cams-table">
          <thead className="bg-[#FAFAFA] border-b border-[#E2E8F0]">
            <tr>
              <th className="px-5 py-3 text-left">Activo</th>
              <th className="px-5 py-3 text-right whitespace-nowrap">Cantidad</th>
              <th className="px-5 py-3 text-left whitespace-nowrap">Días para su disponibilidad</th>
            </tr>
          </thead>
          <tbody data-testid="results-table">
            {loading && <tr><td colSpan={3} className="px-5 py-8 text-center text-sm text-[#5C6A7D]">Cargando…</td></tr>}
            {!loading && assets.length === 0 && (
              <tr><td colSpan={3} className="px-5 py-12 text-center text-sm text-[#5C6A7D]">Ningún activo coincide con tus filtros.</td></tr>
            )}
            {!loading && assets.map((a) => {
              const av = availabilityInfo(a);
              return (
                <tr
                  key={a.id}
                  onClick={() => nav(`/assets/${a.id}`)}
                  className="border-b border-[#E2E8F0] last:border-0 hover:bg-[#16A34A]/[0.04] cursor-pointer transition-colors"
                  data-testid={`asset-row-${a.id}`}
                >
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      {a.photos?.[0] ? (
                        <img src={fileUrl(a.photos[0])} alt="" className="w-11 h-11 object-cover rounded-lg border border-[#E2E8F0] shrink-0" />
                      ) : (
                        <div className="w-11 h-11 bg-[#EAF0F6] rounded-lg flex items-center justify-center text-[#5C6A7D] shrink-0"><Package size={16} /></div>
                      )}
                      <span className="font-semibold text-[#0A2540] hover:underline">{a.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-right font-mono text-sm whitespace-nowrap">{a.quantity} {a.unit}</td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full border ${TONE[av.tone]}`}>
                      {av.text}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
